import { Component, OnInit } from '@angular/core';
import { Referrer } from '../../classes/referrer';
import { ReferrerService } from '../../services/referrer';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GeneralModalComponent } from '../../modals/general/general-modal/general-modal.component';
import { ReferrerCreateModalComponent } from '../../modals/referrer/referrer-create-modal/referrer-create-modal.component';
import { AuthService } from '../../services/auth';
import { User } from '../../classes/user';
import {
    AdvancedSearchField,
    AdvancedSearchState,
    countActiveRules,
    emptyAdvancedState
} from '../../utils/advancedSearch';
import { loadAdvancedState, saveAdvancedState } from '../../utils/filterStateStorage';
import { csvFileName, downloadCsv } from '../../utils/csvExport';

@Component({
    selector: 'app-referrer',
    templateUrl: './referrer.component.html',
    styleUrls: ['./referrer.component.css']
})
export class ReferrerComponent implements OnInit {
    paginationConfig = {
        itemsPerPage: 25,
        currentPage: 1
    };
    columnLayoutToken = 0;
    paginationParams = {
        autoHide: true,
        maxSize: 7,
        responsive: true
    };
    paginationLabels = {
        previousLabel: 'Precedente',
        nextLabel: 'Successiva',
        screenReaderPaginationLabel: 'Paginazione',
        screenReaderPageLabel: 'pagina',
        screenReaderCurrentLabel: `Sei sulla pagina`
    };

    globalSearchFilterValue: string = '';
    idFilterValue: string = '';
    nameFilterValue: string = '';
    surnameFilterValue: string = '';
    emailFilterValue: string = '';

    private readonly screenKey = 'segnalatori';
    readonly advancedFields: AdvancedSearchField<Referrer>[] = [
        {key: 'id', label: 'ID Segnalatore', type: 'number', value: referrer => referrer.IDReferrer},
        {key: 'name', label: 'Nome', type: 'text', value: referrer => referrer.Name},
        {key: 'surname', label: 'Cognome', type: 'text', value: referrer => referrer.Surname},
        {key: 'email', label: 'Email', type: 'text', value: referrer => referrer.Email}
    ];
    advancedState: AdvancedSearchState = emptyAdvancedState();

    user: User = new User();
    referrers: Array<Referrer> = [];

    constructor(private router: Router,
                private authService: AuthService,
                private modalService: NgbModal,
                private referrerService: ReferrerService) {
    }

    ngOnInit(): void {
        this.advancedState = loadAdvancedState(this.screenKey, this.advancedFields) ?? emptyAdvancedState();
        this.authService.isUserSignedIn()
            .subscribe({
                next: () => {
                    this.authService.getUser()
                        .subscribe({
                            next: (user: any) => {
                                this.user = user;
                                if (this.user.FKIDUserRole !== 1) {
                                    this.router.navigate(['/dashboard']);
                                    return;
                                }
                                this.loadReferrers();
                            },
                            error: () => {
                                this.router.navigate(['/login']);
                            }
                        });
                },
                error: () => {
                    this.router.navigate(['/login']);
                }
            });
    }

    private loadReferrers(): void {
        this.referrerService.getAll()
            .subscribe({
                next: (referrers: Array<Referrer>) => {
                    this.referrers = referrers;
                    this.relockColumns();
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    pageChanged(event: any) {
        this.paginationConfig.currentPage = event;
        this.relockColumns();
    }

    private relockColumns(): void {
        this.columnLayoutToken++;
    }

    onFilterChange(): void {
        this.paginationConfig.currentPage = 1;
    }

    clearFilters(): void {
        this.globalSearchFilterValue = '';
        this.idFilterValue = '';
        this.nameFilterValue = '';
        this.surnameFilterValue = '';
        this.emailFilterValue = '';
        this.advancedState = emptyAdvancedState();
        saveAdvancedState(this.screenKey, this.advancedState, this.advancedFields);
        this.paginationConfig.currentPage = 1;
    }

    onAdvancedStateChange(state: AdvancedSearchState): void {
        this.advancedState = state;
        saveAdvancedState(this.screenKey, state, this.advancedFields);
        this.paginationConfig.currentPage = 1;
    }

    exportCsv(referrers: Array<Referrer>): void {
        downloadCsv(
            csvFileName('segnalatori'),
            ['ID Segnalatore', 'Nome', 'Cognome', 'Email'],
            referrers.map(referrer => [
                referrer.IDReferrer ?? '',
                referrer.Name || '',
                referrer.Surname || '',
                referrer.Email || ''
            ])
        );
    }

    hasActiveFilters(): boolean {
        return !!(
            this.globalSearchFilterValue
            || this.idFilterValue
            || this.nameFilterValue
            || this.surnameFilterValue
            || this.emailFilterValue
            || countActiveRules(this.advancedState, this.advancedFields) > 0
        );
    }

    referrerLabel(referrer: Referrer): string {
        return `${referrer.Name || ''} ${referrer.Surname || ''}`.trim();
    }

    goToReferrerDetail(idReferrer: number | undefined) {
        if (idReferrer == null) {
            return;
        }
        this.router.navigate(['/segnalatori/' + idReferrer]);
    }

    openCreateReferrerModal() {
        const modalRef = this.modalService.open(ReferrerCreateModalComponent, { size: 'xl', centered: true });
        modalRef.result.then((referrerBack: Referrer) => {
            if (referrerBack?.IDReferrer !== undefined) {
                this.loadReferrers();
            }
        }, () => {});
    }

    openDeleteReferrerModal(referrer: Referrer) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Cancellazione <strong>Segnalatore</strong>';
        modalRef.componentInstance.text = `Confermi la cancellazione del segnalatore "${this.referrerLabel(referrer)}"?`;
        modalRef.result.then(() => {
            this.referrerService.delete(referrer.IDReferrer)
                .subscribe({
                    next: () => {
                        this.referrers = this.referrers.filter((currentReferrer: Referrer) => currentReferrer.IDReferrer !== referrer.IDReferrer);
                    },
                    error: (err: HttpErrorResponse) => {
                        const message = err.error && err.error.message
                            ? err.error.message
                            : 'Non è stato possibile eliminare il segnalatore.';
                        const errorModal = this.modalService.open(GeneralModalComponent);
                        errorModal.componentInstance.title = 'Eliminazione <strong>Segnalatore</strong>';
                        errorModal.componentInstance.text = message;
                    }
                });
        }, () => {});
    }
}
